import { forwardRef, useMemo } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import styled from '@mui/material/styles/styled';
import Tooltip from '@mui/material/Tooltip';

import { MenuContainerV2 } from '../MenuContainerV2/MenuContainerV2';

export const SelectMenuV2 = forwardRef(
  (
    {
      disabled,
      menuLabel,
      onChange,
      options,
      selected,
      withNoneOption,
      popoverPositionX = 'left',
      withoutLabel,
      hidden,
      customHead,
    },
    ref,
  ) => {
    const valueToLabelMap = useMemo(
      () =>
        options?.reduce((acc, curr) => {
          acc[curr.value] = curr.label;

          return acc;
        }, {}),
      [options],
    );

    return (
      <MenuContainerV2
        menuLabel={menuLabel}
        selectedLabel={
          ((selected || selected === 0) && valueToLabelMap?.[selected]) ||
          (withNoneOption ? 'None' : '')
        }
        disabled={disabled || !options.length}
        ref={ref}
        popoverPositionX={popoverPositionX}
        withoutLabel={withoutLabel}
        hidden={hidden}
        customHead={customHead}
      >
        {({ close }) => (
          <MenuList disablePadding>
            {withNoneOption && (
              <MenuItem
                onClick={() => {
                  onChange('');
                  close();
                }}
              >
                None
              </MenuItem>
            )}
            {options.map((option) => {
              const isSelected = selected === option.value;

              return (
                <Tooltip
                  arrow
                  key={option.value}
                  disableHoverListener={!option.disabled}
                  title={option.tooltip}
                  placement={option.tooltipPosition}
                >
                  <MenuItem
                    onClick={() => {
                      onChange(option.value);
                      close();
                    }}
                    selected={isSelected}
                    disabled={option.disabled}
                  >
                    {option.data ? (
                      <GridMenuItemRow>
                        <MenuItemLabel>{option.label}</MenuItemLabel>
                        <Box>{typeof option.data !== 'object' ? option.data : ''}</Box>
                        <MenuItemCheckIcon>
                          {isSelected && <CheckIcon fontSize="small" sx={{ mt: '-3px' }} />}
                        </MenuItemCheckIcon>
                      </GridMenuItemRow>
                    ) : (
                      <FlexMenuItemRow>
                        {option.label}
                        {isSelected && <CheckIcon color="primary" fontSize="small" />}
                      </FlexMenuItemRow>
                    )}
                  </MenuItem>
                </Tooltip>
              );
            })}
          </MenuList>
        )}
      </MenuContainerV2>
    );
  },
);

SelectMenuV2.displayName = 'SelectMenu';

const MenuItemLabel = styled(Box)(() => ({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  paddingRight: 1,
}));

const MenuItemCheckIcon = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const FlexMenuItemRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  color: theme.palette.success.dark,
  minHeight: '40px',
  minWidth: '180px',
}));

const GridMenuItemRow = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 80px 24px',
  alignItems: 'center',
  columnGap: '20px',
  width: '100%',
}));
